import { useState } from 'react';
import { Calendar as CalendarIcon, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { MonthPickerPanel } from '@/components/MonthPicker';
import { formatDateInput, parseDateValue } from '@/lib/dates';
import { fromMonthKey, toMonthKey } from '@/lib/months';
import { DISPLAY_MONTH_NAMES } from '@/lib/payments';
import { cn } from '@/lib/utils';

type DateInputProps = {
  id?: string;
  name?: string;
  value: string;
  onChange: (value: string) => void;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
};

const toDisplayDate = (date: Date) => {
  const dd = String(date.getDate()).padStart(2, '0');
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const yyyy = date.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
};

export function DateInput({
  id,
  name,
  value,
  onChange,
  onBlur,
  placeholder = 'dd/mm/yyyy',
  disabled,
  className,
}: DateInputProps) {
  const [open, setOpen] = useState(false);
  const selectedDate = parseDateValue(value) ?? undefined;
  // The month the calendar shows; clicking its title switches to the month / year picker.
  const [shownMonth, setShownMonth] = useState<Date>(selectedDate ?? new Date());
  const [pickingMonth, setPickingMonth] = useState(false);

  const handleOpenChange = (next: boolean) => {
    if (next) {
      setShownMonth(selectedDate ?? new Date());
      setPickingMonth(false);
    }
    setOpen(next);
  };

  return (
    <div
      className={cn(
        'flex h-10 box-border overflow-hidden rounded-sm border border-gray-200 bg-white transition-colors focus-within:border-[#4369a0] focus-within:ring-1 focus-within:ring-[#4369a0]/25',
        disabled && 'bg-gray-50',
        className
      )}
    >
      <input
        id={id}
        name={name}
        inputMode="numeric"
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(formatDateInput(e.target.value))}
        onBlur={onBlur}
        placeholder={placeholder}
        maxLength={10}
        className="h-full min-h-0 w-full bg-transparent px-3 text-sm text-gray-900 outline-none disabled:cursor-not-allowed disabled:text-gray-600"
      />
      <Popover open={open} onOpenChange={handleOpenChange}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            disabled={disabled}
            className="h-full w-10 shrink-0 rounded-none border-l border-gray-200 text-gray-500 hover:bg-[#4369a0]/10 hover:text-[#4369a0]"
            aria-label="Open date picker"
          >
            <CalendarIcon className="h-4 w-4" />
          </Button>
        </PopoverTrigger>
        <PopoverContent align="end" className="z-[100] w-auto p-0" sideOffset={6}>
          {pickingMonth ? (
            <div className="p-3">
              <MonthPickerPanel
                value={toMonthKey({ month: shownMonth.getMonth(), year: shownMonth.getFullYear() })}
                onSelect={(monthKey) => {
                  const { year, month } = fromMonthKey(monthKey);
                  setShownMonth(new Date(year, month, 1));
                  setPickingMonth(false);
                }}
              />
            </div>
          ) : (
            <Calendar
              mode="single"
              selected={selectedDate}
              month={shownMonth}
              onMonthChange={setShownMonth}
              onSelect={(date) => {
                if (!date) return;
                onChange(toDisplayDate(date));
                setOpen(false);
              }}
              components={{
                CaptionLabel: ({ displayMonth }) => (
                  <button
                    type="button"
                    onClick={() => setPickingMonth(true)}
                    className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-sm font-semibold text-gray-900 hover:bg-gray-100"
                    aria-label="Choose month and year"
                  >
                    {DISPLAY_MONTH_NAMES[displayMonth.getMonth()]} {displayMonth.getFullYear()}
                    <ChevronDown className="h-3.5 w-3.5 text-gray-500" />
                  </button>
                ),
              }}
            />
          )}
        </PopoverContent>
      </Popover>
    </div>
  );
}
